import { useRef } from 'react';

type UseLatest = <T>(value: T) => { current: T };

/** Literally the equivalent of using `useRef` to safely access changing values
 *  inside another hook without dependancies being updated. This wrapper makes it
 *  easier to differentiate between other ref usages e.g. DOM maniupulation vs
 *  using latest values for rerender safety.
 *
 * You can wrap useLatest around values as well as callbacks.
 *
 * @example
 * // Lets consider `mousePosition` changes often either via state or through props
 * const latestMousePosition = useLatest(mousePosition);
 *
 * useEffect(() => {
 *     // we only cared about `isMovementEnabled` changes, and don't want `mousePosition`
 *     // to trigger this useEffect, so we can use latestMousePosition which is stable
 *
 *     if (isMovementEnabled) {
 *          // since `latestMousePosition` is a ref, we must access the mutable value via `.current`
 *          doSomething(latestMousePosition.current)
 *     }
 * }, [latestMousePosition, isMovementEnabled])
 *
 * */
export const useLatest: UseLatest = (value) => {
	const latestValueRef = useRef(value);
	latestValueRef.current = value;

	return latestValueRef;
};
